// Professional Super Credits invoice modal.
//
// Fetches /student/credits/invoice/{order_id}, renders a polished
// "tax-invoice"-style popup, and provides a "Download PDF" action that
// streams the same invoice from /credits/invoice/{order_id}/pdf.
//
// Single source of truth lives on the backend (_build_invoice in
// student_routes.py) so the popup numbers can never drift from the PDF.
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from './studentApi';
import { toast } from 'sonner';
import { X, Download, FileText, ShieldCheck, Loader2 } from 'lucide-react';
import { notifyError, friendlyError } from '../../lib/errors';
import Loader from '../../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceModal({ orderId, onClose }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Backend writes the order doc inside the verify call BEFORE the API
    // response returns, so the invoice GET should succeed immediately.
    // We still defend against eventual-consistency by retrying once after
    // 500ms on the first failure rather than auto-closing the modal.
    const fetchOnce = async () => {
      const r = await api.get(`/student/credits/invoice/${orderId}`);
      return r.invoice;
    };
    (async () => {
      try {
        let inv;
        try {
          inv = await fetchOnce();
        } catch (e1) {
          await new Promise(res => setTimeout(res, 500));
          inv = await fetchOnce();
        }
        if (!cancelled) setInvoice(inv);
      } catch (e) {
        if (!cancelled) setError(friendlyError(e, 'Invoice not available'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('desky_token') || '';
      const res = await fetch(`${API}/student/credits/invoice/${orderId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice?.invoice_no || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (e) {
      notifyError(e, 'Could not download PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="invoice-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-lg max-w-2xl w-full max-h-[92vh] overflow-auto relative"
        onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button onClick={onClose} aria-label="Close"
          data-testid="invoice-close"
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-black/5 z-10">
          <X className="w-4 h-4 text-slate-500" />
        </button>

        {loading || (!invoice && !error) ? (
          <div className="p-16 flex flex-col items-center gap-3 text-slate-500">
            <Loader label="Fetching invoice…" />
          </div>
        ) : error ? (
          <div className="p-12 flex flex-col items-center gap-3 text-center" data-testid="invoice-error">
            <div className="w-12 h-12 rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Invoice not ready yet</div>
              <div className="text-xs text-slate-500 mt-1 max-w-sm">{error}</div>
            </div>
            <Button onClick={onClose} variant="outline" className="rounded-full mt-2">Close</Button>
          </div>
        ) : (
          <>
            {/* Header band */}
            <div className="px-7 pt-7 pb-5 text-white rounded-t-3xl"
              style={{ background: 'linear-gradient(135deg,#0f766e 0%,#34A853 100%)' }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold opacity-80">
                    Deskey · Tax Invoice
                  </div>
                  <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {invoice.invoice_no}
                  </h2>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20">
                    <ShieldCheck className="w-3 h-3" /> Paid via {invoice.provider}
                  </div>
                </div>
                <div className="text-right text-xs opacity-90 leading-relaxed">
                  <div>Issued <b>{(invoice.issued_at || '').slice(0, 10)}</b></div>
                  <div className="opacity-80 mt-0.5 font-mono text-xs">
                    Ref · {invoice.payment_reference}
                  </div>
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="px-7 pt-5 pb-3 grid grid-cols-2 gap-6 text-xs">
              <div>
                <div className="text-xs font-semibold text-slate-400">From</div>
                <div className="font-semibold text-slate-900 mt-1">{invoice.company.name}</div>
                {invoice.company.address_lines.map((l, i) => (
                  <div key={i} className="text-slate-500">{l}</div>
                ))}
                <div className="text-slate-500 mt-0.5">GSTIN · <span className="font-mono">{invoice.company.gstin}</span></div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400">Bill To</div>
                <div className="font-semibold text-slate-900 mt-1">{invoice.customer.name}</div>
                <div className="text-slate-500">{invoice.customer.email}</div>
                {invoice.customer.phone && <div className="text-slate-500">{invoice.customer.phone}</div>}
              </div>
            </div>

            {/* Line items */}
            <div className="px-7 mt-4">
              <table className="w-full text-xs" data-testid="invoice-items">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                    <th className="py-2 font-semibold">Description</th>
                    <th className="py-2 font-semibold text-right w-12">Qty</th>
                    <th className="py-2 font-semibold text-right w-24">Unit</th>
                    <th className="py-2 font-semibold text-right w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-3 text-slate-900 font-medium">{it.description}</td>
                      <td className="py-3 text-right">{it.qty}</td>
                      <td className="py-3 text-right font-mono">{it.unit_price.toFixed(2)}</td>
                      <td className="py-3 text-right font-mono">{it.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="px-7 mt-3 flex justify-end">
              <div className="w-72 text-xs space-y-1.5">
                <Row label="Subtotal" value={`${invoice.totals.subtotal.toFixed(2)}`} />
                <Row label={invoice.totals.tax_label}
                  value={`${invoice.totals.tax_amount.toFixed(2)}`} />
                <div className="h-px bg-slate-200 my-2" />
                <Row label="Total Paid"
                  value={`${invoice.currency} ${invoice.totals.total.toFixed(2)}`}
                  bold accent />
              </div>
            </div>

            {/* Footer / actions */}
            <div className="px-7 pt-5 pb-7 mt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-500 leading-snug">
                Order ID · <span className="font-mono">{invoice.order_id}</span><br />
                Support · <a href={`mailto:${invoice.company.support_email}`} className="text-emerald-600 font-semibold">{invoice.company.support_email}</a>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose} className="rounded-full text-xs" data-testid="invoice-close-btn">
                  Close
                </Button>
                <Button onClick={downloadPdf} disabled={downloading}
                  data-testid="invoice-download-pdf"
                  className="rounded-full text-xs font-semibold px-4 text-white"
                  style={{ background: '#0f766e' }}>
                  {downloading
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Preparing…</>
                    : <><Download className="w-3.5 h-3.5 mr-1" /> Download PDF</>}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={`${bold ? 'font-semibold text-slate-900' : 'text-slate-500'} ${accent ? 'text-base' : ''}`}>
        {label}
      </span>
      <span className={`font-mono ${bold ? 'font-semibold' : 'text-slate-900'} ${accent ? 'text-emerald-600 text-base' : ''}`}>
        {value}
      </span>
    </div>
  );
}

// Small inline pill for re-use elsewhere
export function InvoicePill({ orderId, onClick }) {
  return (
    <button onClick={onClick}
      data-testid={`invoice-pill-${orderId}`}
      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border hover:bg-emerald-50 transition"
      style={{ borderColor: 'var(--st-border)', color: 'var(--st-primary)' }}>
      <FileText className="w-3 h-3" /> Invoice
    </button>
  );
}
